import type { TitularFounder } from '@ops/contracts'

import { Card, Rule, Stack } from '../../ui'
import { resaltarCifras } from './prosa'

/**
 * El titular en prosa, arriba de todo: cómo está la empresa (o el área) y qué
 * frena, en oraciones generadas desde el mismo payload que muestran las tablas.
 *
 * Injerto 1 del briefing ejecutivo. El veredicto va grande; los tres tramos
 * ("Lo que va bien", "Lo que frena", "Lo que no se lee") en prosa gris con las
 * cifras en negro. Ni un número acá está escrito a mano: todos vienen de la
 * función pura que recibe el payload.
 */
export interface TitularProps {
  titular: TitularFounder
  /** Período en palabras, ej. "julio 2026". Va en el rótulo. */
  periodo: string
}

interface Tramo {
  rotulo: string
  texto: string
  testId: string
}

export function Titular({ titular, periodo }: TitularProps) {
  const tramos: readonly Tramo[] = [
    {
      rotulo: 'Lo que va bien',
      texto: titular.loQueVaBien,
      testId: 'titular-va-bien',
    },
    {
      rotulo: 'Lo que frena',
      texto: titular.loQueFrena,
      testId: 'titular-frena',
    },
    {
      rotulo: 'Lo que no se lee',
      texto: titular.loQueNoSeLee,
      testId: 'titular-no-se-lee',
    },
  ]

  return (
    <Card as="section" ariaLabel={`Titular de ${periodo}`} padding="lg">
      <p className="rotulo mb-2">Titular de {periodo}</p>

      <p
        data-testid="titular-veredicto"
        className="max-w-prosa text-seccion leading-snug text-muted-strong [&_b]:font-semibold [&_b]:text-text"
      >
        {resaltarCifras(titular.veredicto)}
      </p>

      <Rule spacing="none" className="my-4" />

      <Stack gap="md">
        {tramos.map((tramo) => (
          <div key={tramo.rotulo} className="min-w-0">
            <p className="rotulo mb-1">{tramo.rotulo}</p>
            <p
              data-testid={tramo.testId}
              className="max-w-prosa text-meta leading-relaxed text-muted-strong [&_b]:font-semibold [&_b]:text-text"
            >
              {resaltarCifras(tramo.texto)}
            </p>
          </div>
        ))}
      </Stack>
    </Card>
  )
}
